import React, { useState, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  FlatList,
  TextInput,
  Platform,
  ScrollView,
} from 'react-native';
import { InsetModal as Modal } from '../InsetModal';
import { Ionicons } from '@expo/vector-icons';
import { META_TEMPLATE_LANGUAGES } from '../../utils/metaTemplateLanguages';
import { colors, spacing, typography } from '../../theme';
import { designTokens } from '../../theme/design-system';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { withAlpha } from '../../theme';

interface LanguagePickerProps {
  value: string;
  onChange: (code: string) => void;
  label?: string;
  error?: string;
}

const LanguageRow: React.FC<{
  item: { code: string; label: string };
  selected: boolean;
  onSelect: (code: string) => void;
}> = ({ item, selected, onSelect }) => {
  const styles = useThemedStyles(getStyles);
  return (
  <TouchableOpacity
    style={[styles.item, selected && styles.itemSelected]}
    onPress={() => onSelect(item.code)}
    activeOpacity={0.7}
  >
    <View style={styles.itemBody}>
      <Text style={[styles.itemName, selected && styles.itemNameSelected]}>{item.label}</Text>
      <Text style={styles.itemCode}>{item.code}</Text>
    </View>
    {selected && <Ionicons name="checkmark" size={18} color={colors.primary} />}
  </TouchableOpacity>
);
};

export const LanguagePicker: React.FC<LanguagePickerProps> = ({
  value,
  onChange,
  label = 'Language',
  error,
}) => {
  const styles = useThemedStyles(getStyles);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const selected = useMemo(() => META_TEMPLATE_LANGUAGES.find((l) => l.code === value), [value]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return META_TEMPLATE_LANGUAGES;
    return META_TEMPLATE_LANGUAGES.filter(
      (l) => l.label.toLowerCase().includes(q) || l.code.toLowerCase().includes(q)
    );
  }, [search]);

  const handleSelect = (code: string) => {
    onChange(code);
    setOpen(false);
    setSearch('');
  };

  // ─── Web: inline expanded list ─────────────────────────────────────────────
  if (Platform.OS === 'web') {
    return (
      <View style={styles.wrapper}>
        {label ? <Text style={styles.label}>{label}</Text> : null}

        <TouchableOpacity
          style={[styles.trigger, error ? styles.triggerError : undefined, open && styles.triggerOpen]}
          onPress={() => setOpen((v) => !v)}
          activeOpacity={0.7}
        >
          <View style={styles.triggerLeft}>
            <Text style={styles.name}>{selected?.label ?? value}</Text>
            <Text style={styles.code}>{value}</Text>
          </View>
          <Ionicons name={open ? 'chevron-up' : 'chevron-down'} size={16} color={colors.textLight} />
        </TouchableOpacity>

        {error ? <Text style={styles.errorText}>{error}</Text> : null}

        {open && (
          <View style={styles.webDropdown}>
            <View style={styles.webSearchRow}>
              <Ionicons name="search-outline" size={15} color={colors.textLight} />
              <TextInput
                style={styles.webSearchInput}
                placeholder="Search language..."
                placeholderTextColor={colors.textLight}
                value={search}
                onChangeText={setSearch}
                autoCorrect={false}
                autoCapitalize="none"
                autoFocus
              />
              {search.length > 0 && (
                <TouchableOpacity onPress={() => setSearch('')}>
                  <Ionicons name="close-circle" size={15} color={colors.textLight} />
                </TouchableOpacity>
              )}
            </View>

            <ScrollView style={styles.webList} nestedScrollEnabled showsVerticalScrollIndicator={false}>
              {filtered.map((item) => (
                <LanguageRow
                  key={item.code}
                  item={item}
                  selected={item.code === value}
                  onSelect={handleSelect}
                />
              ))}
              {filtered.length === 0 && (
                <View style={styles.emptyRow}>
                  <Text style={styles.emptyText}>No languages found</Text>
                </View>
              )}
            </ScrollView>
          </View>
        )}
      </View>
    );
  }

  // ─── Mobile: bottom-sheet modal ────────────────────────────────────────────
  return (
    <>
      <View style={styles.wrapper}>
        {label ? <Text style={styles.label}>{label}</Text> : null}
        <TouchableOpacity
          style={[styles.trigger, error ? styles.triggerError : undefined]}
          onPress={() => setOpen(true)}
          activeOpacity={0.7}
        >
          <View style={styles.triggerLeft}>
            <Text style={styles.name}>{selected?.label ?? value}</Text>
            <Text style={styles.code}>{value}</Text>
          </View>
          <Ionicons name="chevron-down" size={16} color={colors.textLight} />
        </TouchableOpacity>
        {error ? <Text style={styles.errorText}>{error}</Text> : null}
      </View>

      <Modal visible={open} animationType="slide" transparent>
        <View style={styles.overlay}>
          <View style={styles.sheet}>
            <View style={styles.sheetHeader}>
              <Text style={styles.sheetTitle}>Select Language</Text>
              <TouchableOpacity
                onPress={() => { setOpen(false); setSearch(''); }}
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              >
                <Ionicons name="close" size={22} color={colors.textDark} />
              </TouchableOpacity>
            </View>

            <View style={styles.searchRow}>
              <Ionicons name="search-outline" size={16} color={colors.textLight} />
              <TextInput
                style={styles.searchInput}
                placeholder="Search language..."
                placeholderTextColor={colors.textLight}
                value={search}
                onChangeText={setSearch}
                autoCorrect={false}
                autoCapitalize="none"
              />
              {search.length > 0 && (
                <TouchableOpacity onPress={() => setSearch('')}>
                  <Ionicons name="close-circle" size={16} color={colors.textLight} />
                </TouchableOpacity>
              )}
            </View>

            <FlatList
              data={filtered}
              keyExtractor={(item) => item.code}
              renderItem={({ item }) => (
                <LanguageRow
                  item={item}
                  selected={item.code === value}
                  onSelect={handleSelect}
                />
              )}
              ItemSeparatorComponent={() => <View style={styles.separator} />}
              keyboardShouldPersistTaps="handled"
              showsVerticalScrollIndicator={false}
            />
          </View>
        </View>
      </Modal>
    </>
  );
};

const getStyles = () => StyleSheet.create({
  wrapper: { gap: 4 },
  label: {
    fontSize: typography.fontSize.sm,
    color: colors.textDark,
    fontFamily: typography.fontFamily.medium,
    marginBottom: 4,
  },
  trigger: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderWidth: 1.5,
    borderColor: colors.border,
    borderRadius: designTokens.borderRadius.md,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    backgroundColor: colors.background.surface,
    gap: spacing.sm,
  },
  triggerOpen: {
    borderColor: colors.primary,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    borderBottomWidth: 0,
  },
  triggerError: { borderColor: colors.error },
  triggerLeft: { flex: 1, flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  name: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.semibold,
    color: colors.textDark,
  },
  code: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.regular,
    color: colors.textLight,
  },
  errorText: {
    fontSize: typography.fontSize.xs,
    color: colors.error,
    fontFamily: typography.fontFamily.regular,
  },

  // ─── Web inline dropdown ───────────────────────────────────────────────────
  webDropdown: {
    borderWidth: 1.5,
    borderTopWidth: 0,
    borderColor: colors.primary,
    borderBottomLeftRadius: designTokens.borderRadius.md,
    borderBottomRightRadius: designTokens.borderRadius.md,
    backgroundColor: colors.background.surface,
    overflow: 'hidden',
    ...designTokens.shadows.sm,
  },
  webSearchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    gap: spacing.sm,
    backgroundColor: colors.lightGray,
  },
  webSearchInput: {
    flex: 1,
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.regular,
    color: colors.textDark,
    outlineStyle: 'none',
  } as any,
  webList: {
    maxHeight: 320,
  },
  emptyRow: {
    padding: spacing.lg,
    alignItems: 'center',
  },
  emptyText: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    fontFamily: typography.fontFamily.regular,
  },

  // ─── Mobile modal ──────────────────────────────────────────────────────────
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
    justifyContent: 'flex-end',
  },
  sheet: {
    backgroundColor: colors.background.surface,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '80%',
    ...designTokens.shadows.lg,
  },
  sheetHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.lg,
    paddingBottom: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  sheetTitle: {
    fontSize: typography.fontSize.lg,
    fontFamily: typography.fontFamily.semibold,
    color: colors.textDark,
  },
  searchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    margin: spacing.md,
    paddingHorizontal: spacing.md,
    paddingVertical: Platform.OS === 'ios' ? spacing.sm : 0,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1.5,
    borderColor: colors.border,
    backgroundColor: colors.lightGray,
    gap: spacing.sm,
  },
  searchInput: {
    flex: 1,
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.regular,
    color: colors.textDark,
    paddingVertical: Platform.OS === 'android' ? spacing.sm : 0,
  },

  // ─── Shared row ───────────────────────────────────────────────────────────
  item: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
    gap: spacing.md,
  },
  itemSelected: { backgroundColor: withAlpha(colors.primary, 0.06) },
  itemBody: { flex: 1 },
  itemName: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.semibold,
    color: colors.textDark,
  },
  itemNameSelected: { color: colors.primary },
  itemCode: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.regular,
    color: colors.textLight,
  },
  separator: { height: 1, backgroundColor: withAlpha(colors.border, 0.38) },
});
